import { toHtml, isEmptyValue, escapeHtml } from '../editor/content.js';

export function normalizeAnswer(s) {
  return String(s ?? '')
    .normalize('NFC')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export function isCorrect(typed, expected) {
  return normalizeAnswer(typed) === normalizeAnswer(expected);
}

/** Character diff (LCS). Returns [{ type: 'same'|'missing'|'extra', text }]. */
export function diffChars(typed, expected) {
  const a = [...String(typed ?? '')];
  const b = [...String(expected ?? '')];
  const n = a.length;
  const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        a[i].toLowerCase() === b[j].toLowerCase()
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  const push = (type, ch) => {
    const last = out[out.length - 1];
    if (last?.type === type) last.text += ch;
    else out.push({ type, text: ch });
  };
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i].toLowerCase() === b[j].toLowerCase()) {
      push('same', b[j]);
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      push('extra', a[i++]);
    } else {
      push('missing', b[j++]);
    }
  }
  while (i < n) push('extra', a[i++]);
  while (j < m) push('missing', b[j++]);
  return out;
}

export function diffHtml(typed, expected) {
  return diffChars(typed, expected)
    .map((p) => `<span class="diff-${p.type}">${escapeHtml(p.text)}</span>`)
    .join('');
}

export const typein = {
  id: 'typein',
  name: 'Type the answer',
  short: 'Type it',
  description: 'Spell it out. Your answer is checked letter by letter.',
  icon: 'type',
  fields: [
    {
      key: 'front',
      label: 'Question',
      hint: 'What you’ll be asked.',
      kind: 'rich',
      required: true,
      placeholder: 'Valve between the left atrium and left ventricle',
    },
    {
      key: 'answer',
      label: 'Correct answer',
      hint: 'Exactly what you should type. Capital letters don’t matter.',
      kind: 'text',
      required: true,
      placeholder: 'mitral',
    },
    { key: 'extra', label: 'Extra info', hint: 'Shown with the answer.', kind: 'rich' },
  ],
  ords: () => [0],
  cardLabel: () => 'Card',
  validate: (f) =>
    isEmptyValue(f.front)
      ? 'Prompt is empty.'
      : !String(f.answer ?? '').trim()
        ? 'Expected answer is empty.'
        : null,
  render: (note) => {
    const front = toHtml(note.fields.front);
    const extra = isEmptyValue(note.fields.extra) ? '' : toHtml(note.fields.extra);
    return {
      front,
      back: extra,
      mode: 'type',
      expected: String(note.fields.answer ?? ''),
    };
  },
};
